import React, { useState, useRef, useEffect, cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { User, Briefcase, ExternalLink } from 'lucide-react';

/**
 * EventTooltip - Hover tooltip for calendar events showing client/case info
 *
 * Props:
 * - event: the calendar event object
 * - children: the event content to render
 * - asWrapper: if true, clones children and adds handlers instead of wrapping in a div
 */
const EventTooltip = ({ event, children, asWrapper = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  const resource = event.resource || {};
  const hasCase = resource.caseId && resource.caseTitle;
  const hasClient = resource.clientId && resource.clientName;
  const showTooltip = hasCase || hasClient;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Don't render tooltip if no case/client info
  if (!showTooltip) {
    return children;
  }

  const handleMouseEnter = (e) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const tooltipWidth = 250;
      const tooltipHeight = 100;

      let top = rect.top - tooltipHeight - 8;
      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);

      if (top < 10) {
        top = rect.bottom + 8;
      }

      if (left < 10) {
        left = 10;
      } else if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }

      setPosition({ top, left });
    }

    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150);
  };

  const handleTooltipMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsVisible(false);
  };

  const tooltip = isVisible && createPortal(
    <div
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[200px] max-w-[280px]"
      style={{
        top: position.top,
        left: position.left,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {hasClient && (
        <Link
          to={`/clients/${resource.clientId}`}
          className="flex items-center gap-2 p-2 -m-1 mb-1 rounded hover:bg-gray-50 group"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500">Client</div>
            <div className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
              {resource.clientName}
            </div>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
        </Link>
      )}

      {hasCase && (
        <Link
          to={`/cases/${resource.caseId}`}
          className="flex items-center gap-2 p-2 -m-1 rounded hover:bg-gray-50 group"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500">Case</div>
            <div className="text-sm font-medium text-gray-900 truncate group-hover:text-amber-600">
              {resource.caseTitle}
            </div>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
        </Link>
      )}
    </div>,
    document.body
  );

  // Wrapper mode: clone children and add handlers directly
  if (asWrapper && isValidElement(children)) {
    return (
      <>
        {cloneElement(children, {
          ref: containerRef,
          onMouseEnter: (e) => {
            handleMouseEnter(e);
            // Call original handler if it exists
            if (children.props.onMouseEnter) {
              children.props.onMouseEnter(e);
            }
          },
          onMouseLeave: (e) => {
            handleMouseLeave();
            if (children.props.onMouseLeave) {
              children.props.onMouseLeave(e);
            }
          },
        })}
        {tooltip}
      </>
    );
  }

  // Default mode: wrap in a div
  return (
    <>
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="h-full"
      >
        {children}
      </div>
      {tooltip}
    </>
  );
};

export default EventTooltip;
